import { adminCopy } from "../copy/admin";
import type { JimReadAdmin, ReadSuggestion } from "../types";
import { escapeHtml } from "../utils";

type ReadsDeps = {
  createRead: (clauseNumber: string, body: string) => Promise<JimReadAdmin>;
  updateRead: (id: string, body: string, updatedAt: string) => Promise<JimReadAdmin>;
  approveRead: (id: string, updatedAt: string) => Promise<JimReadAdmin>;
  deleteRead: (id: string) => Promise<void>;
  reload: () => Promise<void>;
  onError: (message: string) => void;
  onUseSuggestion?: (clauseNumber: string) => void;
};

function approveControlsMarkup(readId: string, updatedAt: string): string {
  const id = escapeHtml(readId);
  return `<button type="button" class="ghost approveReadButton" data-id="${id}">${adminCopy.approveRead}</button>
    <div class="inline-confirm" data-approve-id="${id}" hidden>
      <span>${adminCopy.confirmApproveRead}</span>
      <button type="button" class="ghost confirmApproveRead" data-id="${id}" data-updated-at="${escapeHtml(updatedAt)}">${adminCopy.confirmApproveReadAction}</button>
      <button type="button" class="ghost cancelApproveRead" data-id="${id}">${adminCopy.table.cancel}</button>
    </div>`;
}

function deleteControlsMarkup(readId: string): string {
  const id = escapeHtml(readId);
  return `<button type="button" class="ghost danger deleteReadButton" data-id="${id}">${adminCopy.deleteRead}</button>
    <div class="inline-confirm" data-delete-id="${id}" hidden>
      <span>${adminCopy.confirmDeleteRead}</span>
      <button type="button" class="ghost danger confirmDeleteRead" data-id="${id}">${adminCopy.confirmDeleteReadAction}</button>
      <button type="button" class="ghost cancelDeleteRead" data-id="${id}">${adminCopy.table.cancel}</button>
    </div>`;
}

function readMarkup(read: JimReadAdmin): string {
  const approved = read.status === "approved";
  const reviewer = approved
    ? `${adminCopy.reviewedBy} ${escapeHtml(read.reviewedByUsername || adminCopy.unknownReviewer)} · ${escapeHtml(read.reviewedAt || "")}`
    : `${adminCopy.draftUpdated} ${escapeHtml(read.updatedAt)}`;
  return `<article class="jim-read-row" data-read-id="${escapeHtml(read.id)}">
    <h4>${escapeHtml(read.clauseNumber)} — ${escapeHtml(read.title)}</h4>
    <p class="jim-read-body">${escapeHtml(read.body)}</p>
    <p class="muted">${reviewer}</p>
    <div class="row-actions">
      <button type="button" class="ghost editReadButton" data-id="${escapeHtml(read.id)}" data-approved="${String(approved)}" data-updated-at="${escapeHtml(read.updatedAt)}">${adminCopy.editRead}</button>
      ${approved ? "" : approveControlsMarkup(read.id, read.updatedAt)}
      ${deleteControlsMarkup(read.id)}
    </div>
  </article>`;
}

function suggestionMarkup(item: ReadSuggestion): string {
  const hits = adminCopy.searchHits.replace("{count}", String(item.searchHits));
  return `<li>
    <strong>${escapeHtml(item.clauseNumber)}</strong> ${escapeHtml(item.title)}
    <span>${escapeHtml(hits)}</span>
    <button type="button" class="ghost useSuggestionButton" data-clause-number="${escapeHtml(item.clauseNumber)}">${adminCopy.useSuggestion}</button>
  </li>`;
}

export function readsSectionMarkup(reads: JimReadAdmin[], suggestions: ReadSuggestion[]): string {
  const drafts = reads.filter((read) => read.status === "draft");
  const approved = reads.filter((read) => read.status === "approved");
  const draftList = drafts.length ? drafts.map(readMarkup).join("") : `<p class="muted">${adminCopy.noDraftReads}</p>`;
  const approvedList = approved.length ? approved.map(readMarkup).join("") : `<p class="muted">${adminCopy.noApprovedReads}</p>`;
  const suggestionList = suggestions.length
    ? `<ul class="read-suggestions">${suggestions.map(suggestionMarkup).join("")}</ul>`
    : `<p class="muted">${adminCopy.noReadSuggestions}</p>`;
  return `<section class="panel workspace-panel">
    <h2>${adminCopy.readsHeading}</h2>
    <div class="reads-grid">
      <div>
        <h3>${adminCopy.reviewQueue}</h3>
        <h4>${adminCopy.awaitingApproval}</h4>
        ${draftList}
        <h4>${adminCopy.approvedReads}</h4>
        ${approvedList}
      </div>
      <aside>
        <h3>${adminCopy.needsRead}</h3>
        ${suggestionList}
      </aside>
    </div>
    <h3>${adminCopy.createReadHeading}</h3>
    <form id="createReadForm" class="stack read-form">
      <label>${adminCopy.clauseNumber}<input name="clauseNumber" required /></label>
      <label>${adminCopy.readBody}<textarea name="body" required minlength="20" maxlength="4000"></textarea></label>
      <button>${adminCopy.createRead}</button>
    </form>
  </section>`;
}

export function draftReadCount(reads: JimReadAdmin[]): number {
  return reads.filter((read) => read.status === "draft").length;
}

export function createReadMarkup(): string {
  return `
    <form id="createReadForm" class="stack read-form">
      <label>${adminCopy.clauseNumber}<input name="clauseNumber" required /></label>
      <label>${adminCopy.readBody}<textarea name="body" required minlength="20" maxlength="4000"></textarea></label>
      <button>${adminCopy.createRead}</button>
    </form>
  `;
}

export function reviewQueueMarkup(reads: JimReadAdmin[]): string {
  const drafts = reads.filter((read) => read.status === "draft");
  const approved = reads.filter((read) => read.status === "approved");
  return `
    <h3>${adminCopy.awaitingApproval}</h3>
    ${drafts.length ? drafts.map(readMarkup).join("") : `<p class="muted">${adminCopy.noDraftReads}</p>`}
    <h3>${adminCopy.approvedReads}</h3>
    ${approved.length ? approved.map(readMarkup).join("") : `<p class="muted">${adminCopy.noApprovedReads}</p>`}
  `;
}

export function needsReadMarkup(suggestions: ReadSuggestion[]): string {
  return suggestions.length
    ? `<ul class="read-suggestions">${suggestions.map(suggestionMarkup).join("")}</ul>`
    : `<p class="muted">${adminCopy.noReadSuggestions}</p>`;
}

function showConfirm(button: HTMLButtonElement, selector: string): void {
  button.hidden = true;
  document.querySelector<HTMLElement>(selector)?.removeAttribute("hidden");
}

function beginEdit(button: HTMLButtonElement, deps: ReadsDeps, fail: (error: unknown, fallback: string) => void): void {
  const row = document.querySelector<HTMLElement>(`.jim-read-row[data-read-id="${button.dataset.id}"]`);
  if (!row) return;
  const body = row.querySelector<HTMLElement>(".jim-read-body")?.textContent || "";
  const textarea = Object.assign(document.createElement("textarea"), { className: "jim-read-edit", value: body });
  row.querySelector(".jim-read-body")?.replaceWith(textarea);
  const warning = button.dataset.approved === "true" ? `<p class="muted">${adminCopy.editApprovedWarning}</p>` : "";
  button.insertAdjacentHTML(
    "afterend",
    `${warning}<button type="button" class="ghost saveReadButton" data-id="${escapeHtml(button.dataset.id || "")}" data-updated-at="${escapeHtml(button.dataset.updatedAt || "")}">${adminCopy.saveRead}</button><button type="button" class="ghost cancelReadEdit">${adminCopy.table.cancel}</button>`
  );
  button.remove();
  row.querySelector<HTMLButtonElement>(".saveReadButton")?.addEventListener("click", async (save) => {
    const trigger = save.currentTarget as HTMLButtonElement;
    try {
      await deps.updateRead(trigger.dataset.id || "", row.querySelector<HTMLTextAreaElement>(".jim-read-edit")?.value || "", trigger.dataset.updatedAt || "");
      await deps.reload();
    } catch (error) {
      fail(error, adminCopy.updateReadFailure);
    }
  });
  row.querySelector<HTMLButtonElement>(".cancelReadEdit")?.addEventListener("click", () => deps.reload());
}

export function bindReadsSection(deps: ReadsDeps): void {
  const fail = (error: unknown, fallback: string) => deps.onError(error instanceof Error ? error.message : fallback);

  document.querySelector<HTMLFormElement>("#createReadForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    try {
      await deps.createRead(String(form.get("clauseNumber") || ""), String(form.get("body") || ""));
      await deps.reload();
    } catch (error) {
      fail(error, adminCopy.createReadFailure);
    }
  });

  document.querySelectorAll<HTMLButtonElement>(".useSuggestionButton").forEach((button) => {
    button.addEventListener("click", () => {
      if (deps.onUseSuggestion) {
        deps.onUseSuggestion(button.dataset.clauseNumber || "");
        return;
      }
      const input = document.querySelector<HTMLInputElement>("#createReadForm input[name='clauseNumber']");
      if (input) {
        input.value = button.dataset.clauseNumber || "";
        input.focus();
      }
    });
  });

  document.querySelectorAll<HTMLButtonElement>(".editReadButton").forEach((button) => {
    button.addEventListener("click", () => beginEdit(button, deps, fail));
  });

  document.querySelectorAll<HTMLButtonElement>(".approveReadButton").forEach((button) => {
    button.addEventListener("click", () => showConfirm(button, `.inline-confirm[data-approve-id="${button.dataset.id}"]`));
  });

  document.querySelectorAll<HTMLButtonElement>(".deleteReadButton").forEach((button) => {
    button.addEventListener("click", () => showConfirm(button, `.inline-confirm[data-delete-id="${button.dataset.id}"]`));
  });

  document.querySelectorAll<HTMLButtonElement>(".cancelApproveRead, .cancelDeleteRead").forEach((button) => {
    button.addEventListener("click", () => deps.reload());
  });

  document.querySelectorAll<HTMLButtonElement>(".confirmApproveRead").forEach((button) => {
    button.addEventListener("click", async () => {
      try {
        await deps.approveRead(button.dataset.id || "", button.dataset.updatedAt || "");
        await deps.reload();
      } catch (error) {
        fail(error, adminCopy.approveReadFailure);
      }
    });
  });

  document.querySelectorAll<HTMLButtonElement>(".confirmDeleteRead").forEach((button) => {
    button.addEventListener("click", async () => {
      try {
        await deps.deleteRead(button.dataset.id || "");
        await deps.reload();
      } catch (error) {
        fail(error, adminCopy.deleteReadFailure);
      }
    });
  });
}
